import React, { useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { api, useAuth } from '../App';
import { toast } from '../components/ui/sonner';
import { Save, Plus, X, MapPin, Clock, ExternalLink, Upload, Linkedin, Mail, Camera, Building, Briefcase, CheckCircle, Send } from 'lucide-react';

// Research methods and sectors constants
const RESEARCH_METHODS = [
  "Focus Groups", "IDIs", "Ethnography", "Online Communities", "Survey Design",
  "Conjoint", "MaxDiff", "Segmentation", "Report Writing", "Synthesis",
  "Workshop Facilitation", "Translation", "Back-translation", "Cultural Adaptation",
  "Diary Studies", "UX Research", "Card Sorting", "Eye-tracking", "Social Listening",
  "Text Analytics", "Quantitative Analysis", "Qualitative Analysis"
];

const SECTORS = [
  "FMCG", "Healthcare", "Beauty", "Financial Services", "Telecoms",
  "Retail", "Automotive", "Luxury", "B2B", "Tech", "Education",
  "Travel & Hospitality", "Media & Entertainment", "Real Estate", "Agriculture"
];

const ARRANGEMENT_TYPES = [
  { value: "freelance", label: "Freelance / Per-project" },
  { value: "part_time", label: "Part-time" },
  { value: "full_time", label: "Full-time" },
  { value: "per_project", label: "Per Project" },
  { value: "internship", label: "Internship" }
];

const RESEARCHER_TYPES = [
  { value: "freelancer", label: "Freelancer" },
  { value: "agency", label: "Agency" },
  { value: "consultant", label: "Consultant" },
  { value: "academic", label: "Academic" },
  { value: "intern", label: "Intern" }
];

const AVAILABILITY_OPTIONS = [
  { value: "available", label: "Open / Available" },
  { value: "busy", label: "Busy" },
  { value: "not_available", label: "Not Available" }
];

export default function MyProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState({
    full_name: '',
    headline: '',
    bio: '',
    location: '',
    photo_url: '',
    company_name: '',
    designation: '',
    company_email: '',
    company_email_verified: false,
    company_website: '',
    methods: [],
    sectors: [],
    tools: [],
    languages: [],
    certifications: [],
    day_rate_min: '',
    day_rate_max: '',
    hourly_rate: '',
    monthly_salary: '',
    stipend: '',
    arrangement_type: 'freelance',
    availability: 'available',
    researcher_type: 'freelancer',
    linkedin_url: '',
    portfolio_links: [],
    cv_url: '',
    experience_years: '',
    email_notifications: true,
    notification_email: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newTool, setNewTool] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const [newCert, setNewCert] = useState('');
  const [newLink, setNewLink] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get('/profile/me');
        setProfile({
          full_name: response.data.full_name || user?.name || '',
          headline: response.data.headline || '',
          bio: response.data.bio || '',
          location: response.data.location || '',
          photo_url: response.data.photo_url || user?.picture || '',
          company_name: response.data.company_name || '',
          designation: response.data.designation || '',
          company_email: response.data.company_email || '',
          company_email_verified: response.data.company_email_verified || false,
          company_website: response.data.company_website || '',
          methods: response.data.methods || [],
          sectors: response.data.sectors || [],
          tools: response.data.tools || [],
          languages: response.data.languages || [],
          certifications: response.data.certifications || [],
          day_rate_min: response.data.day_rate_min || '',
          day_rate_max: response.data.day_rate_max || '',
          hourly_rate: response.data.hourly_rate || '',
          monthly_salary: response.data.monthly_salary || '',
          stipend: response.data.stipend || '',
          arrangement_type: response.data.arrangement_type || 'freelance',
          availability: response.data.availability || 'available',
          researcher_type: response.data.researcher_type || 'freelancer',
          linkedin_url: response.data.linkedin_url || '',
          portfolio_links: response.data.portfolio_links || [],
          cv_url: response.data.cv_url || '',
          experience_years: response.data.experience_years || '',
          email_notifications: response.data.email_notifications !== false,
          notification_email: response.data.notification_email || user?.email || ''
        });
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [user]);
  
  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };
  
  const toggleArrayItem = (field, item) => {
    setProfile(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };
  
  const addItem = (field, value, setter) => {
    if (value.trim()) {
      setProfile(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
      setter('');
    }
  };
  
  const removeItem = (field, index) => {
    setProfile(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };
  
  const uploadFile = async (file, folder, isRaw = false) => {
    setUploading(true);
    try {
      // Get signature from backend
      const sigResponse = await api.get(`/cloudinary/signature?folder=${folder}&resource_type=${isRaw ? 'raw' : 'image'}`);
      const sig = sigResponse.data;
      
      // Upload to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);
      
      const uploadUrl = `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${isRaw ? 'raw' : 'image'}/upload`;
      
      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        body: formData
      });
      
      const result = await uploadResponse.json();
      return result.secure_url;
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload file');
      return null;
    } finally {
      setUploading(false);
    }
  };
  
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    
    const url = await uploadFile(file, 'profiles/photos');
    if (url) {
      handleChange('photo_url', url);
    }
  };
  
  const handleCVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      alert('Please select a PDF file');
      return;
    }
    
    const url = await uploadFile(file, 'profiles/cv', true);
    if (url) {
      handleChange('cv_url', url);
    }
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        ...profile,
        experience_years: profile.experience_years ? parseInt(profile.experience_years) : null,
        day_rate_min: profile.day_rate_min ? parseFloat(profile.day_rate_min) : null,
        day_rate_max: profile.day_rate_max ? parseFloat(profile.day_rate_max) : null,
        hourly_rate: profile.hourly_rate ? parseFloat(profile.hourly_rate) : null,
        monthly_salary: profile.monthly_salary ? parseFloat(profile.monthly_salary) : null,
        stipend: profile.stipend ? parseFloat(profile.stipend) : null
      };
      await api.put('/profile/me', updateData);
      toast.success('Profile saved successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  const sendVerificationCode = async () => {
    if (!profile.company_email) {
      alert('Please enter your company email first');
      return;
    }
    
    setSendingCode(true);
    try {
      await api.post('/verification/send-email-code', { company_email: profile.company_email });
      setShowCodeInput(true);
      alert('Verification code sent to your company email!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setSendingCode(false);
    }
  };
  
  const verifyEmailCode = async () => {
    if (!verificationCode) {
      alert('Please enter the verification code');
      return;
    }
    
    setVerifyingCode(true);
    try {
      await api.post('/verification/verify-email-code', { code: verificationCode });
      setProfile(prev => ({ ...prev, company_email_verified: true }));
      setShowCodeInput(false);
      setVerificationCode('');
      alert('Company email verified successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setVerifyingCode(false);
    }
  };
  
  if (loading) {
    return (
      <DashboardLayout title="My Profile">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]"></div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout title="My Profile">
      <div className="max-w-4xl">
        {/* Photo & Basic Info */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <div className="flex items-start gap-6 mb-6">
            <div className="relative">
              {profile.photo_url ? (
                <img 
                  src={profile.photo_url} 
                  alt={profile.full_name}
                  className="w-24 h-24 rounded-full object-cover"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-[#FFF0EB] flex items-center justify-center text-[#FF6B35] font-bold text-2xl">
                  {profile.full_name?.[0]?.toUpperCase() || user?.name?.[0]?.toUpperCase()}
                </div>
              )}
              <label className="absolute bottom-0 right-0 w-8 h-8 bg-[#FF6B35] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#E85D2C] transition-colors">
                <Camera size={16} className="text-white" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            </div>
            <div className="flex-1">
              <p className="text-neutral-500 text-sm mb-2">{user?.email}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    data-testid="fullname-input"
                    placeholder="e.g., Priya Nair"
                    value={profile.full_name}
                    onChange={(e) => handleChange('full_name', e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    <MapPin size={14} className="inline mr-1" />
                    Location
                  </label>
                  <input
                    type="text"
                    data-testid="location-input"
                    placeholder="e.g., Mumbai, India"
                    value={profile.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-1">Professional Headline</label>
            <input
              type="text"
              data-testid="headline-input"
              placeholder="e.g., Senior Qual Moderator | FMCG"
              value={profile.headline}
              onChange={(e) => handleChange('headline', e.target.value)}
              className="w-full px-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Bio</label>
            <textarea
              data-testid="bio-input"
              placeholder="Tell clients about your experience..."
              value={profile.bio}
              onChange={(e) => handleChange('bio', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none resize-none"
            />
          </div>
        </div>
        
        {/* Methods */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>Methods</h3>
          <p className="text-sm text-neutral-500 mb-3">Select the research methods you are interested in</p>
          <div className="flex flex-wrap gap-2">
            {RESEARCH_METHODS.map((method) => (
              <button
                key={method}
                type="button"
                onClick={() => toggleArrayItem('methods', method)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  profile.methods.includes(method)
                    ? 'bg-[#FF6B35] text-white'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                {method}
              </button>
            ))}
          </div>
        </div>
        
        {/* Sectors */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>Sectors</h3>
          <p className="text-sm text-neutral-500 mb-3">Select the industry sectors you are interested in</p>
          <div className="flex flex-wrap gap-2">
            {SECTORS.map((sector) => (
              <button
                key={sector}
                type="button"
                onClick={() => toggleArrayItem('sectors', sector)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  profile.sectors.includes(sector)
                    ? 'bg-[#FF6B35] text-white'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                {sector}
              </button>
            ))}
          </div>
        </div>
        
        {/* Tools & Languages */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>Tools</h3>
              <p className="text-sm text-neutral-500 mb-3">Software and tools you use</p>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  data-testid="tool-input"
                  placeholder="e.g., Zoom, Dovetail, Miro"
                  value={newTool}
                  onChange={(e) => setNewTool(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('tools', newTool, setNewTool))}
                  className="flex-1 px-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none"
                />
                <button
                  type="button"
                  onClick={() => addItem('tools', newTool, setNewTool)}
                  className="btn-researcher px-3 py-2"
                >
                  <Plus size={18} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {profile.tools.map((tool, i) => (
                  <span key={i} className="px-3 py-1 bg-neutral-100 text-neutral-700 text-sm flex items-center gap-2">
                    {tool}
                    <button onClick={() => removeItem('tools', i)} className="hover:text-red-500"><X size={14} /></button>
                  </span>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>Languages</h3>
              <p className="text-sm text-neutral-500 mb-3">Languages you can work in</p>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  data-testid="language-input"
                  placeholder="e.g., English, Hindi"
                  value={newLanguage}
                  onChange={(e) => setNewLanguage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('languages', newLanguage, setNewLanguage))}
                  className="flex-1 px-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none"
                />
                <button
                  type="button"
                  onClick={() => addItem('languages', newLanguage, setNewLanguage)}
                  className="btn-researcher px-3 py-2"
                >
                  <Plus size={18} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {profile.languages.map((lang, i) => (
                  <span key={i} className="px-3 py-1 bg-neutral-100 text-neutral-700 text-sm flex items-center gap-2">
                    {lang}
                    <button onClick={() => removeItem('languages', i)} className="hover:text-red-500"><X size={14} /></button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Rates & Availability */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>Compensation & Availability</h3>
          
          {/* Arrangement & Type selects first — they drive which rate fields show */}
          <div className="grid sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Arrangement Type</label>
              <select
                data-testid="arrangement-select"
                value={profile.arrangement_type}
                onChange={(e) => handleChange('arrangement_type', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none bg-white"
              >
                {ARRANGEMENT_TYPES.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Availability</label>
              <select
                data-testid="availability-select"
                value={profile.availability}
                onChange={(e) => handleChange('availability', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none bg-white"
              >
                {AVAILABILITY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Type</label>
              <select
                data-testid="type-select"
                value={profile.researcher_type}
                onChange={(e) => handleChange('researcher_type', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none bg-white"
              >
                {RESEARCHER_TYPES.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Adaptive rate fields based on arrangement type */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Freelance / Part-time / Per-project: show day rate + hourly rate */}
            {['freelance', 'part_time', 'per_project'].includes(profile.arrangement_type) && (
              <>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Day Rate (Min) ₹</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">₹</span>
                    <input type="number" data-testid="dayrate-min-input" placeholder="40000" value={profile.day_rate_min} onChange={(e) => handleChange('day_rate_min', e.target.value)} className="w-full pl-8 pr-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Day Rate (Max) ₹</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">₹</span>
                    <input type="number" data-testid="dayrate-max-input" placeholder="60000" value={profile.day_rate_max} onChange={(e) => handleChange('day_rate_max', e.target.value)} className="w-full pl-8 pr-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Hourly Rate ₹</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">₹</span>
                    <input type="number" data-testid="hourlyrate-input" placeholder="5000" value={profile.hourly_rate} onChange={(e) => handleChange('hourly_rate', e.target.value)} className="w-full pl-8 pr-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none" />
                  </div>
                </div>
              </>
            )}

            {/* Full-time: show expected monthly salary */}
            {profile.arrangement_type === 'full_time' && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Expected Monthly Salary ₹</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">₹</span>
                  <input type="number" data-testid="monthly-salary-input" placeholder="80000" value={profile.monthly_salary} onChange={(e) => handleChange('monthly_salary', e.target.value)} className="w-full pl-8 pr-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none" />
                </div>
              </div>
            )}

            {/* Internship: show expected monthly stipend */}
            {profile.arrangement_type === 'internship' && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Expected Monthly Stipend ₹</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">₹</span>
                  <input type="number" data-testid="stipend-input" placeholder="15000" value={profile.stipend} onChange={(e) => handleChange('stipend', e.target.value)} className="w-full pl-8 pr-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none" />
                </div>
              </div>
            )}

            {/* Experience years — always shown */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Experience (Years)</label>
              <input type="number" data-testid="experience-input" placeholder="5" value={profile.experience_years} onChange={(e) => handleChange('experience_years', e.target.value)} className="w-full px-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none" />
            </div>
          </div>
        </div>
        
        {/* Certifications */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>Certifications</h3>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              data-testid="cert-input"
              placeholder="Add a certification..."
              value={newCert}
              onChange={(e) => setNewCert(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('certifications', newCert, setNewCert))}
              className="flex-1 px-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none"
            />
            <button type="button" onClick={() => addItem('certifications', newCert, setNewCert)} className="btn-researcher px-4 py-2">
              <Plus size={18} />
            </button>
          </div>
          <ul className="space-y-2">
            {profile.certifications.map((cert, i) => (
              <li key={i} className="flex items-center justify-between bg-neutral-50 px-3 py-2">
                <span>{cert}</span>
                <button onClick={() => removeItem('certifications', i)} className="text-neutral-400 hover:text-red-500"><X size={16} /></button>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Links & Documents */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>Links & Documents</h3>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              <Linkedin size={14} className="inline mr-1" />
              LinkedIn Profile URL
            </label>
            <input
              type="url"
              data-testid="linkedin-input"
              placeholder="https://linkedin.com/in/yourprofile"
              value={profile.linkedin_url}
              onChange={(e) => handleChange('linkedin_url', e.target.value)}
              className="w-full px-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none"
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-1">Portfolio Links</label>
            <div className="flex gap-2 mb-3">
              <input
                type="url"
                data-testid="link-input"
                placeholder="https://..."
                value={newLink}
                onChange={(e) => setNewLink(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('portfolio_links', newLink, setNewLink))}
                className="flex-1 px-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none"
              />
              <button type="button" onClick={() => addItem('portfolio_links', newLink, setNewLink)} className="btn-researcher px-4 py-2">
                <Plus size={18} />
              </button>
            </div>
            <ul className="space-y-2">
              {profile.portfolio_links.map((link, i) => (
                <li key={i} className="flex items-center justify-between bg-neutral-50 px-3 py-2">
                  <a href={link} target="_blank" rel="noopener noreferrer" className="text-[#FF6B35] hover:underline flex items-center gap-1 truncate">
                    <ExternalLink size={14} />{link}
                  </a>
                  <button onClick={() => removeItem('portfolio_links', i)} className="text-neutral-400 hover:text-red-500 flex-shrink-0"><X size={16} /></button>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              <Upload size={14} className="inline mr-1" />
              CV / Resume (PDF)
            </label>
            {profile.cv_url ? (
              <div className="flex items-center gap-3 bg-neutral-50 px-3 py-2">
                <a href={profile.cv_url} target="_blank" rel="noopener noreferrer" className="text-[#FF6B35] hover:underline flex items-center gap-1">
                  <ExternalLink size={14} />View CV
                </a>
                <button onClick={() => handleChange('cv_url', '')} className="text-neutral-400 hover:text-red-500 ml-auto"><X size={16} /></button>
              </div>
            ) : (
              <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-neutral-200 cursor-pointer hover:border-[#FF6B35] transition-colors">
                <Upload size={20} className="text-neutral-400" />
                <span className="text-neutral-600">{uploading ? 'Uploading...' : 'Choose file'}</span>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleCVUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            )}
          </div>
        </div>
        
        {/* Company / Agency Details */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>
            <Building size={18} className="inline mr-2" />
            Company / Agency Details
          </h3>
          <p className="text-sm text-neutral-500 mb-4">If you work with an agency or company, add details here for verification.</p>
          
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Company / Agency Name</label>
              <input
                type="text"
                data-testid="company-name-input"
                placeholder="e.g., Kantar, IPSOS, Nielsen"
                value={profile.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                <Briefcase size={14} className="inline mr-1" />
                Designation
              </label>
              <input
                type="text"
                data-testid="designation-input"
                placeholder="e.g., Research Director, Senior Analyst"
                value={profile.designation}
                onChange={(e) => handleChange('designation', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none"
              />
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-1">Company Website</label>
            <input
              type="url"
              data-testid="company-website-input"
              placeholder="https://www.yourcompany.com"
              value={profile.company_website}
              onChange={(e) => handleChange('company_website', e.target.value)}
              className="w-full px-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              <Mail size={14} className="inline mr-1" />
              Official Company Email
            </label>
            <div className="flex gap-2">
              <input
                type="email"
                data-testid="company-email-input"
                placeholder="you@yourcompany.com"
                value={profile.company_email}
                onChange={(e) => handleChange('company_email', e.target.value)}
                disabled={profile.company_email_verified}
                className="flex-1 px-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none disabled:bg-neutral-50"
              />
              {profile.company_email_verified ? (
                <span className="flex items-center gap-1 px-4 py-2 bg-green-50 text-green-700 text-sm font-medium">
                  <CheckCircle size={16} /> Verified
                </span>
              ) : (
                <button
                  type="button"
                  data-testid="send-verification-btn"
                  onClick={sendVerificationCode}
                  disabled={sendingCode || !profile.company_email}
                  className="btn-researcher px-4 py-2 flex items-center gap-2 text-sm disabled:opacity-50"
                >
                  {sendingCode ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Send size={14} /> Verify
                    </>
                  )}
                </button>
              )}
            </div>
            <p className="text-xs text-neutral-400 mt-1">Use your official work email (not gmail/yahoo). A verification code will be sent.</p>
            
            {showCodeInput && !profile.company_email_verified && (
              <div className="mt-3 flex gap-2">
                <input
                  type="text"
                  data-testid="verification-code-input"
                  placeholder="Enter 6-digit code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  maxLength={6}
                  className="w-40 px-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none text-center tracking-widest font-mono"
                />
                <button
                  type="button"
                  data-testid="confirm-verification-btn"
                  onClick={verifyEmailCode}
                  disabled={verifyingCode || verificationCode.length !== 6}
                  className="btn-researcher px-4 py-2 text-sm disabled:opacity-50"
                >
                  {verifyingCode ? 'Verifying...' : 'Confirm'}
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Email Notifications */}
        <div className="bg-white border border-neutral-200 p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>
            <Mail size={18} className="inline mr-2" />
            Email Notifications
          </h3>
          
          <div className="flex items-center gap-3 mb-4">
            <input
              type="checkbox"
              id="email-notifications"
              checked={profile.email_notifications}
              onChange={(e) => handleChange('email_notifications', e.target.checked)}
              className="w-5 h-5 accent-[#FF6B35]"
            />
            <label htmlFor="email-notifications" className="text-neutral-700">
              Receive email notifications for application updates
            </label>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Notification Email</label>
            <input
              type="email"
              data-testid="notification-email-input"
              placeholder="your@email.com"
              value={profile.notification_email}
              onChange={(e) => handleChange('notification_email', e.target.value)}
              className="w-full px-3 py-2 border border-neutral-200 focus:border-[#FF6B35] outline-none"
            />
          </div>
        </div>
        
        {/* Save Button */}
        <button
          data-testid="save-profile-btn"
          onClick={handleSave}
          disabled={saving || uploading}
          className="btn-researcher px-8 py-3 flex items-center gap-2"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
          ) : (
            <>
              <Save size={18} />
              Save Profile
            </>
          )}
        </button>
      </div>
    </DashboardLayout>
  );
}
